import { ButtonGroup, Grid, IconButton, makeStyles } from '@material-ui/core'
import { Delete as DeleteIcon, Edit as EditIcon } from '@material-ui/icons'
import { unwrapResult } from '@reduxjs/toolkit'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { uniq } from 'lodash'
import pluralize from 'pluralize'
import { useHistory } from 'react-router-dom'

import { useActionRunner } from 'src/modules/notification'
import {
  Button,
  Container,
  FormikDialog,
  OrganisationName,
  PageTitle,
  ProductList,
  Typography,
} from 'src/modules/ui'
import { useIsMobile } from 'src/modules/ui/utils'
import { formatDate, formatPrice } from 'src/utils'

import { usePurchasing } from '../hooks'
import TotalBar from './TotalBar'

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: 90,
  },
  iconButton: {
    color: 'white',
  },
  productTable: {
    backgroundColor: theme.palette.fadedGreen.main,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    width: '100%',
  },
  supplierHeader: {
    alignItems: 'flex-start',
    [theme.breakpoints.up('mobile')]: {
      display: 'flex',
      justifyContent: 'space-between',
    },
  },
  supplierMinimumOrder: {
    marginRight: theme.spacing(2),
  },
  supplierTotals: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('mobile')]: {
      marginBottom: 0,
    },
  },
  submitButton: {
    marginLeft: theme.spacing(1),
  },
  supplierTable: {
    marginBottom: theme.spacing(2),
  },
  viewSwitch: {
    marginBottom: theme.spacing(3),
  },
}))

const DraftOrder = ({ canSubmit, id, lineItems, total, scheduleLater }) => {
  const classes = useStyles()
  const { deleteDraftOrder, submitDraftOrder } = usePurchasing()
  const [viewBySupplier, setViewBySupplier] = useState(false)
  const history = useHistory()
  const isMobile = useIsMobile()

  const products = lineItems
    ? lineItems.map(lineItem => ({
        ...lineItem.agreementProduct.product,
        supplier: lineItem.agreementProduct.supplier,
        price: lineItem.agreementProduct.price,
        quantity: lineItem.quantity,
      }))
    : []
  const numProducts = products.length
  const numSuppliers = uniq(products.map(p => p.supplier.id)).length

  const [doSubmit, isSubmitting] = useActionRunner(submitDraftOrder, {
    successNotification: {
      image: 'onion',
      message: `Your order has been sent to your ${pluralize(
        'supplier',
        numSuppliers
      )} and will be dispatched soon!`,
    },
    errorNotification: result => {
      try {
        unwrapResult(result)
      } catch (err) {
        if (err.status === 400) {
          const { suppliersWithMinimumOrdersNotMet, unavailableProductIds } =
            err.data

          if (suppliersWithMinimumOrdersNotMet.length > 0) {
            return 'Not all supplier minimum orders have been met'
          } else if (unavailableProductIds.length > 0) {
            return 'Some products are not available for order'
          }
        }
      }
    },
  })
  const [doDelete] = useActionRunner(deleteDraftOrder, {
    successNotification: 'Draft order deleted',
  })

  const handleSubmit = async () => {
    const result = await doSubmit(id)
    try {
      unwrapResult(result)
      history.push(`/purchasing/submitted`)
    } catch (err) {}
  }

  const handleEditDraft = () => {
    history.push(`/purchasing/drafts/${id}/edit`)
  }

  const handleDelete = async () => {
    const result = await doDelete(id)
    try {
      unwrapResult(result)
      history.push(`/purchasing/drafts`)
    } catch (err) {}
  }

  const productsBySupplier = products.reduce((bySupplier, product) => {
    let supplier = bySupplier.find(s => s.id === product.supplier.id)
    if (!supplier) {
      supplier = {
        ...product.supplier,
        products: [],
        total: 0,
      }
      bySupplier.push(supplier)
    }
    supplier.total += product.quantity * product.price
    supplier.products.push(product)
    return bySupplier
  }, [])

  return (
    <Container className={classes.root}>
      <PageTitle>Draft Order</PageTitle>
      <ButtonGroup className={classes.viewSwitch}>
        <Button
          color={viewBySupplier ? 'default' : 'lightGreen'}
          onClick={() => setViewBySupplier(false)}
        >
          By Category
        </Button>
        <Button
          color={viewBySupplier ? 'lightGreen' : 'default'}
          onClick={() => setViewBySupplier(true)}
        >
          By Supplier
        </Button>
      </ButtonGroup>

      {viewBySupplier ? (
        productsBySupplier.map(supplier => (
          <div className={classes.supplierTable} key={supplier.id}>
            <div className={classes.supplierHeader}>
              <OrganisationName {...supplier} nanme={supplier.name} />
              <div className={classes.supplierTotals}>
                {!!supplier.minimumOrder && (
                  <Typography
                    color="textSecondary"
                    className={classes.supplierMinimumOrder}
                  >
                    {formatPrice(supplier.minimumOrder)} minimum
                  </Typography>
                )}
                <Typography> Total {formatPrice(supplier.total)}</Typography>
              </div>
            </div>
            <ProductList
              noCategories
              products={supplier.products}
              showDescription={false}
              showPhoto={false}
              showQuantity
              showSupplierName
              tableClassName={classes.productTable}
            />
          </div>
        ))
      ) : (
        <ProductList
          products={products}
          showDescription={false}
          showPhoto={false}
          showQuantity
          showSupplierName
          tableClassName={classes.productTable}
        />
      )}
      {scheduleLater && scheduleLater !== '' ? (
        <Grid container justifyContent="flex-end" style={{ marginTop: '2rem' }}>
          <Grid item>
            <Typography>
              Requested Delivery Date: {formatDate(scheduleLater)}
            </Typography>
          </Grid>
        </Grid>
      ) : null}

      <TotalBar
        numProducts={numProducts}
        numSuppliers={numSuppliers}
        total={total}
      >
        <div>
          <FormikDialog
            onSubmit={handleDelete}
            submitText="Delete Draft"
            trigger={props => (
              <IconButton className={classes.iconButton} {...props}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          >
            <Typography variant="body2">Delete this draft?</Typography>
          </FormikDialog>
          <IconButton className={classes.iconButton} onClick={handleEditDraft}>
            <EditIcon fontSize="small" />
          </IconButton>
          {canSubmit && (
            <Button
              className={classes.submitButton}
              onClick={handleSubmit}
              color="orange"
              isLoading={isSubmitting}
              size="large"
            >
              Submit Order
              {!isMobile && ` To ${pluralize('Supplier', numSuppliers, true)}`}
            </Button>
          )}
        </div>
      </TotalBar>
    </Container>
  )
}

DraftOrder.propTypes = {
  canSubmit: PropTypes.bool,
  id: PropTypes.number,
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      agreementProduct: PropTypes.object,
      quantity: PropTypes.number,
    })
  ),
}

export default DraftOrder
